/**
 * AdminInvitesPage — super-admin / org-admin invite + analytics dashboard.
 *
 * Lets you create personalised /i/<slug> invite links, baking in iOS / Android
 * URLs and demo credentials per recipient, then track who clicked, on what
 * device, and whether they fired downstream events (install / first_login).
 */
import { useEffect, useState } from "react";
import DashboardLayout from "../../components/DashboardLayout";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../../components/ui/dialog";
import {
  Plus, Link as LinkIcon, Apple, Smartphone, Globe2, Copy, Check, Eye, BarChart3, Loader2, RefreshCcw, Trash2,
} from "lucide-react";
import { toast } from "sonner";

const PUBLIC_BASE = process.env.REACT_APP_BACKEND_URL || "";

export default function AdminInvitesPage() {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [statsFor, setStatsFor] = useState(null);
  const [stats, setStats] = useState(null);
  const [copied, setCopied] = useState(null);

  const [form, setForm] = useState({
    name: "",
    organization: "",
    email: "",
    role: "investor",
    notes: "",
    ios_url: "",
    android_url: "",
    web_url: PUBLIC_BASE,
    demo_email: "",
    demo_password: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/invites");
      setInvites(data || []);
    } catch {
      toast.error("Could not load invites.");
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name) { toast.error("Recipient name is required."); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/invites", form);
      toast.success(`Invite created · /i/${data.slug}`);
      setForm({ ...form, name: "", email: "", organization: "", notes: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create invite.");
    }
    setCreating(false);
  };

  const openStats = async (slug) => {
    setStatsFor(slug);
    setStats(null);
    try {
      const { data } = await api.get(`/invites/${slug}/stats`);
      setStats(data);
    } catch {
      toast.error("Could not load invite stats.");
    }
  };

  const remove = async (slug, name) => {
    if (!window.confirm(`Delete the invite for "${name}"? This cannot be undone — all click tracking for this invite will also be removed.`)) {
      return;
    }
    try {
      await api.delete(`/invites/${slug}`);
      toast.success("Invite deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete invite.");
    }
  };

  const copy = async (key, value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(key);
      toast.success("Copied");
      setTimeout(() => setCopied(null), 1500);
    } catch {
      toast.error("Could not copy");
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto" data-testid="admin-invites-page">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
              Distribution & Investor Tracking
            </p>
            <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tighter text-[#0A192F]">
              Invite links
            </h1>
            <p className="mt-2 text-sm text-slate-600 max-w-2xl">
              Create personalised /i/&lt;slug&gt; links with baked-in install URLs and demo
              credentials. Every click is tracked with platform breakdown and
              downstream install events.
            </p>
          </div>
          <Button onClick={load} variant="outline" className="border-slate-300" data-testid="invites-refresh">
            <RefreshCcw className="h-3.5 w-3.5 mr-1.5" /> Refresh
          </Button>
        </div>

        {/* Create new */}
        <div className="mt-8 rounded-2xl bg-white border border-slate-200 p-6">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-md bg-[#0F4C81]/10 flex items-center justify-center">
              <Plus className="h-4 w-4 text-[#0F4C81]" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-[#0A192F]">Create a new invite</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Recipient details on the left — install URLs and demo creds on the right.
              </p>
            </div>
          </div>

          <div className="mt-6 grid lg:grid-cols-2 gap-6">
            <div className="space-y-3">
              <Field label="RECIPIENT NAME *">
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Aman Kapoor"
                  data-testid="invite-form-name"
                />
              </Field>
              <Field label="ORGANISATION">
                <Input
                  value={form.organization}
                  onChange={(e) => setForm({ ...form, organization: e.target.value })}
                  placeholder="e.g. Sequoia India"
                  data-testid="invite-form-org"
                />
              </Field>
              <Field label="EMAIL (FOR YOUR RECORDS)">
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="aman@sequoiacap.com"
                  data-testid="invite-form-email"
                />
              </Field>
              <Field label="ROLE">
                <select
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm bg-white"
                  data-testid="invite-form-role"
                >
                  <option value="investor">Investor</option>
                  <option value="school">School / Faculty</option>
                  <option value="partner">Partner / NGO</option>
                  <option value="press">Press / Media</option>
                  <option value="other">Other</option>
                </select>
              </Field>
              <Field label="INTERNAL NOTES (NEVER SHOWN TO RECIPIENT)">
                <Textarea
                  rows={2}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="e.g. Met at Bengaluru EdTech meetup; lead from warm intro by Priya."
                  data-testid="invite-form-notes"
                />
              </Field>
            </div>

            <div className="space-y-3">
              <Field label="iOS / TESTFLIGHT URL">
                <Input
                  value={form.ios_url}
                  onChange={(e) => setForm({ ...form, ios_url: e.target.value })}
                  placeholder="https://testflight.apple.com/join/abc123"
                  data-testid="invite-form-ios"
                />
              </Field>
              <Field label="ANDROID .APK URL (FROM EAS)">
                <Input
                  value={form.android_url}
                  onChange={(e) => setForm({ ...form, android_url: e.target.value })}
                  placeholder="https://expo.dev/artifacts/eas/..."
                  data-testid="invite-form-android"
                />
              </Field>
              <Field label="DEMO LOGIN — EMAIL">
                <Input
                  value={form.demo_email}
                  onChange={(e) => setForm({ ...form, demo_email: e.target.value })}
                  placeholder="superadmin@factskoot.com"
                  data-testid="invite-form-demo-email"
                />
              </Field>
              <Field label="DEMO LOGIN — PASSWORD">
                <Input
                  value={form.demo_password}
                  onChange={(e) => setForm({ ...form, demo_password: e.target.value })}
                  placeholder="Admin@12345"
                  data-testid="invite-form-demo-password"
                />
              </Field>
              <Button
                onClick={create}
                disabled={creating || !form.name}
                className="w-full bg-[#D4AF37] text-[#0A192F] hover:bg-[#B5952F] font-bold uppercase tracking-[0.18em]"
                data-testid="invite-form-create"
              >
                {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                Create invite link
              </Button>
            </div>
          </div>
        </div>

        {/* List */}
        <div className="mt-10">
          <h2 className="text-sm uppercase tracking-[0.22em] text-slate-500 font-bold">All invites · {invites.length}</h2>
          {loading ? (
            <div className="mt-6 text-center py-10 text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin mx-auto" />
            </div>
          ) : invites.length === 0 ? (
            <div className="mt-6 rounded-xl border border-dashed border-slate-200 p-10 text-center">
              <LinkIcon className="h-6 w-6 text-slate-400 mx-auto" />
              <p className="mt-3 text-sm text-slate-500">
                No invites yet. Create your first invite link above.
              </p>
            </div>
          ) : (
            <div className="mt-4 grid gap-3">
              {invites.map((iv) => {
                const url = `${PUBLIC_BASE}/i/${iv.slug}`;
                return (
                  <div key={iv.slug} className="rounded-xl bg-white border border-slate-200 p-5 grid sm:grid-cols-12 gap-4 items-center" data-testid={`invite-row-${iv.slug}`}>
                    <div className="sm:col-span-5">
                      <p className="font-semibold text-[#0A192F]">{iv.name}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{iv.organization || "—"} · <span className="uppercase">{iv.role}</span></p>
                    </div>
                    <div className="sm:col-span-3">
                      <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">Clicks</p>
                      <p className="mt-1 font-mono text-2xl font-bold text-[#0F4C81]">{iv.click_count || 0}</p>
                      {iv.last_click_at && <p className="text-[10px] text-slate-400 mt-0.5">Last: {new Date(iv.last_click_at).toLocaleString()} · {iv.last_click_platform || "?"}</p>}
                    </div>
                    <div className="sm:col-span-4 flex flex-wrap items-center gap-2 justify-end">
                      <span className="text-[11px] font-mono text-slate-500 truncate max-w-[180px]">{`/i/${iv.slug}`}</span>
                      <Button size="sm" variant="outline" onClick={() => copy(iv.slug, url)} className="border-slate-300" data-testid={`copy-link-${iv.slug}`}>
                        {copied === iv.slug ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
                      </Button>
                      <Button size="sm" onClick={() => openStats(iv.slug)} className="bg-[#0F4C81] hover:bg-[#0A3860]" data-testid={`stats-${iv.slug}`}>
                        <BarChart3 className="h-3.5 w-3.5 mr-1" /> Stats
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(iv.slug, iv.name)} className="text-red-500 hover:bg-red-50" data-testid={`delete-${iv.slug}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Stats dialog */}
        <Dialog open={!!statsFor} onOpenChange={(open) => { if (!open) { setStatsFor(null); setStats(null); }}}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="h-4 w-4 text-[#0F4C81]" />
                Invite analytics — /i/{statsFor}
              </DialogTitle>
            </DialogHeader>
            {!stats ? (
              <div className="py-12 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-slate-400" /></div>
            ) : (
              <div className="space-y-5">
                <div className="grid grid-cols-3 gap-3">
                  <Stat label="Total clicks" value={stats.totals.clicks} />
                  <Stat label="Events" value={stats.totals.events} />
                  <Stat label="Last click" value={stats.invite.last_click_at ? new Date(stats.invite.last_click_at).toLocaleDateString() : "—"} />
                </div>

                <div>
                  <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">By platform</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {Object.entries(stats.totals.by_platform).map(([k, v]) => (
                      <span key={k} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 font-semibold">
                        {k === "ios" && <Apple className="h-3 w-3" />}
                        {k === "android" && <Smartphone className="h-3 w-3" />}
                        {k !== "ios" && k !== "android" && <Globe2 className="h-3 w-3" />}
                        {k}: {v}
                      </span>
                    ))}
                    {Object.keys(stats.totals.by_platform).length === 0 && <p className="text-xs text-slate-400 italic">No clicks yet.</p>}
                  </div>
                </div>

                <div>
                  <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">Recent activity</p>
                  <div className="mt-2 max-h-72 overflow-y-auto rounded-lg border border-slate-100 divide-y divide-slate-100">
                    {(stats.clicks.length === 0) ? (
                      <p className="text-xs text-slate-400 italic p-3">No activity yet.</p>
                    ) : stats.clicks.slice(0, 50).map((c) => (
                      <div key={c.id} className="px-4 py-2.5 text-xs flex items-center justify-between">
                        <div>
                          <span className="font-mono text-slate-500">{new Date(c.at).toLocaleString()}</span>
                          <span className="ml-2 inline-block px-1.5 py-0.5 bg-slate-100 rounded uppercase font-semibold">{c.platform}</span>
                        </div>
                        <span className="text-slate-400 truncate max-w-[200px]">{c.user_agent}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => { setStatsFor(null); setStats(null); }}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg bg-slate-50 p-3 border border-slate-100">
      <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">{label}</p>
      <p className="mt-1 font-mono text-2xl font-bold text-[#0A192F]">{value}</p>
    </div>
  );
}
